import {
  Connect,
  ConnectCancelEvent,
  ConnectDoneEvent,
  ConnectErrorEvent,
  ConnectEventHandlers,
  ConnectOptions,
} from 'connect-web-sdk'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSWRConfig } from 'swr'
import useSWRMutation from 'swr/mutation'

import { InternalEventType } from '@frame/lib/api/events/types'
import { DirectTask, FinicityPrepData, TrueworkJsStrategy } from '@frame/lib/api/tasks/types'
import { useRootContext } from '@frame/lib/context'
import { useDirectTask } from '@frame/lib/hooks/useDirectTask'
import { useEventHandler } from '@frame/lib/hooks/useEventHandler'
import { routes } from '@frame/routes'
import { PrimitiveDataAttribute } from '@frame/types'

const FINICITY_TIMEOUT_MS = 10000
const FINICITY_TIMEOUT_ERROR_CODE = 1440
const FINICITY_OVERLAY_RGB = 'rgba(255, 255, 255, 0.0)'
const FINICITY_CSS_SELECTOR = `div[data-cy=${PrimitiveDataAttribute.TW_PRIMITIVE_WIDGET}]`

function getTaskId(task?: DirectTask): string {
  if (task?.id) {
    return task?.id
  }
  throw Error('Direct task does not have an id!')
}

export function FinicityConnectWidget() {
  const { actions } = useRootContext()
  const navigate = useNavigate()
  const task = useDirectTask(true)
  const { handleEvent } = useEventHandler()
  const { mutate } = useSWRConfig()
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const prepData = task?.strategy_info?.prep_data ? (task?.strategy_info?.prep_data as FinicityPrepData) : undefined
  const [iframeLaunched, setIframeLaunched] = useState(false)

  const teardown = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    Connect.destroy()
    setIframeLaunched(false)
  }, [])

  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      if (!iframeLaunched) {
        navigate(routes.error)
        handleEvent(InternalEventType.BANK_WIDGET_FAILED_TO_LOAD)
        teardown()
      }
    }, FINICITY_TIMEOUT_MS) // timeout after waiting 10 seconds for the Finicity iframe to load

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [iframeLaunched, handleEvent, navigate, teardown])

  const { trigger: completeDirectTask } = useSWRMutation(
    ['update', 'employer', task?.id],
    () =>
      actions.completeDirectTask({
        directTaskId: getTaskId(task),
        completeDirectTaskBody: {
          strategy: TrueworkJsStrategy.BANK,
          failed: false,
        },
        multiEmployer: false,
      }),
    {
      onSuccess: async () => {
        await mutate(['all', 'tasks'])
      },
    }
  )

  useEffect(() => {
    const eventHandlers: ConnectEventHandlers = {
      onLoad: async () => {
        setIframeLaunched(true)
      },
      onDone: async () => {
        if (task?.strategy_info?.show_employee_company_match_picker) {
          teardown()
          navigate(`${routes.bankIdentifyIncome}/${task.id}`, { replace: true })
          return
        }
        await completeDirectTask()
          .then(() => {
            navigate(routes.complete, { replace: true })
          })
          .catch(() => {
            navigate(routes.error)
          })
          .finally(() => {
            teardown()
          })
      },
      onCancel: async () => {
        if (task?.strategy_info?.show_employee_company_match_picker) {
          const preparedTask = await actions.addStrategyToDirectTask({
            strategy: TrueworkJsStrategy.CREDENTIALS,
            directTaskId: task.id,
          })
          await mutate(['all', 'tasks'])
          if (!preparedTask) {
            throw new Error('Failed to add credentials strategy to task')
          }
          navigate(`${routes.bankConnectedTasks}/${task.id}`, { replace: true })
          teardown()
          return
        }
        navigate(routes.root)
        teardown()
      },
      onError: (event: ConnectErrorEvent) => {
        if (event.code === FINICITY_TIMEOUT_ERROR_CODE) {
          navigate(routes.root)
          handleEvent(InternalEventType.BANK_CONNECT_TIMEOUT)
          teardown()
        }
      },
    }

    const connectOptions: ConnectOptions = {
      overlay: FINICITY_OVERLAY_RGB,
      selector: FINICITY_CSS_SELECTOR,
      popup: false,
    }

    if (!!prepData?.connect_url && !iframeLaunched && !prepData?.__cypress_behavior) {
      // Ensure only single launch
      // Just in case we call teardown first
      teardown()
      Connect.launch(prepData?.connect_url, eventHandlers, connectOptions)
      // Cypress does not support Finicity Connect, so we skip the launch
    } else if (prepData?.__cypress_behavior === 'complete') {
      eventHandlers.onDone({} as ConnectDoneEvent)
    } else if (prepData?.__cypress_behavior === 'abort') {
      eventHandlers.onCancel({} as ConnectCancelEvent)
    }
  }, [
    iframeLaunched,
    task?.strategy_info?.prep_data,
    task?.task_type,
    prepData?.connect_url,
    completeDirectTask,
    handleEvent,
    navigate,
    mutate,
    teardown,
  ]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="mx-auto flex h-full w-auto flex-1 flex-col items-center justify-center text-center">
      <p className="mt-[10rem] w-[200px] text-moss-500">Just a moment...</p>
    </div>
  )
}
